/*
 * Ahoy.js (DesalData fork) — vanilla fetch/DOM; no jQuery dependency.
 * https://github.com/ankane/ahoy.js v0.2.1
 */

import { onPageLoad } from '../modules/page_lifecycle.js';

(function (window) {
  const config = {
    urlPrefix: '',
    visitsUrl: '/visits',
    eventsUrl: '/events',
    cookieDomain: null,
    page: null,
    platform: 'Web',
    useBeacon: false,
    startOnReady: false
  };

  const ahoy = window.ahoy || window.Ahoy || {};

  ahoy.configure = function (options) {
    Object.keys(options).forEach((key) => {
      config[key] = options[key];
    });
  };

  ahoy.configure(ahoy);

  const visitTtl = 4 * 60;
  const visitorTtl = 2 * 365 * 24 * 60;
  const ahoyAcceptTtl = 7 * 24 * 60;
  let isReady = false;
  const queue = [];
  const canStringify = typeof JSON !== 'undefined' && typeof JSON.stringify !== 'undefined';
  let eventQueue = [];

  function visitsUrl() {
    return config.urlPrefix + config.visitsUrl;
  }

  function eventsUrl() {
    return config.urlPrefix + config.eventsUrl;
  }

  function canTrackNow() {
    return (config.useBeacon || config.trackNow) && canStringify && typeof window.navigator.sendBeacon !== 'undefined';
  }

  function setCookie(name, value, ttl) {
    let expires = '';
    let cookieDomain = '';
    if (ttl) {
      const date = new Date();
      date.setTime(date.getTime() + (ttl * 60 * 1000));
      expires = `; expires=${date.toGMTString()}`;
    }
    const domain = config.cookieDomain || config.domain;
    if (domain) cookieDomain = `; domain=${domain}`;
    document.cookie = `${name}=${escape(value)}${expires}${cookieDomain}; path=/`;
  }

  function getCookie(name) {
    const nameEQ = `${name}=`;
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i += 1) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return unescape(c.substring(nameEQ.length, c.length));
    }
    return null;
  }

  function destroyCookie(name) {
    setCookie(name, '', -1);
  }

  function log(message) {
    if (getCookie('ahoy_debug')) window.console.log(message);
  }

  function setReady() {
    let callback;
    while ((callback = queue.shift())) callback();
    isReady = true;
  }

  function ready(callback) {
    if (isReady) callback();
    else queue.push(callback);
  }

  function generateId() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  function saveEventQueue() {
    if (canStringify) setCookie('ahoy_events', JSON.stringify(eventQueue), 1);
  }

  function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }

  function csrfParam() {
    return document.querySelector('meta[name="csrf-param"]')?.content;
  }

  async function sendRequest(url, data, success) {
    if (!canStringify) return;

    const headers = { 'Content-Type': 'application/json; charset=utf-8', Accept: 'application/json' };
    const token = csrfToken();
    if (token) headers['X-CSRF-Token'] = token;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
        credentials: 'same-origin'
      });
      if (response.ok && success) success();
    } catch {
      // analytics should not break the page
    }
  }

  function eventData(event) {
    const data = {
      events: [event],
      visit_token: event.visit_token,
      visitor_token: event.visitor_token
    };
    delete event.visit_token;
    delete event.visitor_token;
    return data;
  }

  function trackEvent(event) {
    ready(() => {
      sendRequest(eventsUrl(), eventData(event), () => {
        for (let i = 0; i < eventQueue.length; i += 1) {
          if (eventQueue[i].id === event.id) {
            eventQueue.splice(i, 1);
            break;
          }
        }
        saveEventQueue();
      });
    });
  }

  function trackEventNow(event) {
    ready(() => {
      const data = eventData(event);
      const param = csrfParam();
      const token = csrfToken();
      if (param && token) data[param] = token;
      const payload = new Blob([JSON.stringify(data)], { type: 'application/json; charset=utf-8' });
      navigator.sendBeacon(eventsUrl(), payload);
    });
  }

  function pagePath() {
    return config.page || window.location.pathname;
  }

  function eventProperties(event) {
    const target = event.currentTarget;
    return {
      tag: target.tagName.toLowerCase(),
      id: target.id || undefined,
      class: target.className || undefined,
      page: pagePath(),
      section: target.closest('[data-section]')?.dataset.section
    };
  }

  function createVisit() {
    isReady = false;

    let visitId = ahoy.getVisitId();
    let visitorId = ahoy.getVisitorId();

    if (visitId !== null && visitorId !== null) {
      log('Active visit');
      setReady();
      return;
    }

    if (!visitId) {
      visitId = generateId();
      setCookie('ahoy_visit', visitId, visitTtl);
    }

    if (getCookie('ahoy_visit')) {
      log('Visit started');

      if (!visitorId) {
        visitorId = generateId();
        setCookie('ahoy_visitor', visitorId, visitorTtl);
      }

      const data = {
        visit_token: visitId,
        visitor_token: visitorId,
        platform: config.platform,
        landing_page: window.location.href,
        screen_width: window.screen.width,
        screen_height: window.screen.height
      };

      if (document.referrer.length > 0) data.referrer = document.referrer;

      log(data);
      sendRequest(visitsUrl(), data, setReady);
    } else {
      log('Cookies disabled');
      setReady();
    }
  }

  ahoy.resetAhoyAcceptCookie = function () {
    setCookie('ahoy_track', true, ahoyAcceptTtl);
  };

  ahoy.getVisitId = ahoy.getVisitToken = function () {
    return getCookie('ahoy_visit');
  };

  ahoy.getVisitorId = ahoy.getVisitorToken = function () {
    return getCookie('ahoy_visitor');
  };

  ahoy.debug = function (enabled) {
    if (enabled === false) destroyCookie('ahoy_debug');
    else setCookie('ahoy_debug', 't', 365 * 24 * 60);
    return true;
  };

  ahoy.track = function (name, properties) {
    const event = {
      id: generateId(),
      name,
      properties: properties || {},
      time: new Date().getTime() / 1000.0
    };

    onPageLoad(() => log(event));

    ready(() => {
      if (!ahoy.getVisitId()) createVisit();

      event.visit_token = ahoy.getVisitId();
      event.visitor_token = ahoy.getVisitorId();

      if (canTrackNow()) trackEventNow(event);
      else {
        eventQueue.push(event);
        saveEventQueue();
        setTimeout(() => trackEvent(event), 1000);
      }
    });
  };

  ahoy.trackView = function (additionalProperties) {
    const properties = {
      url: window.location.href,
      title: document.title,
      page: pagePath()
    };

    if (additionalProperties) {
      Object.keys(additionalProperties).forEach((propName) => {
        properties[propName] = additionalProperties[propName];
      });
    }
    ahoy.track('$view', properties);
  };

  ahoy.trackClicks = function () {
    document.addEventListener('click', (event) => {
      const target = event.target.closest('a, button, input[type=submit]');
      if (!target) return;

      const properties = eventProperties({ currentTarget: target });
      properties.text = properties.tag === 'input'
        ? target.value
        : (target.textContent || '').replace(/[\s\r\n]+/g, ' ').trim();
      properties.href = target.getAttribute('href');
      ahoy.track('$click', properties);
    });
  };

  ahoy.trackSubmits = function () {
    document.addEventListener('submit', (event) => {
      if (event.target.tagName !== 'FORM') return;
      ahoy.track('$submit', eventProperties({ currentTarget: event.target }));
    });
  };

  ahoy.trackChanges = function () {
    document.addEventListener('change', (event) => {
      const target = event.target.closest('input, textarea, select');
      if (!target) return;
      ahoy.track('$change', eventProperties({ currentTarget: target }));
    });
  };

  ahoy.trackAll = function () {
    ahoy.trackView();
    ahoy.trackClicks();
    ahoy.trackSubmits();
    ahoy.trackChanges();
  };

  try {
    eventQueue = JSON.parse(getCookie('ahoy_events') || '[]');
  } catch {
    // ignore
  }

  eventQueue.forEach((queuedEvent) => trackEvent(queuedEvent));

  ahoy.start = function () {
    ahoy.resetAhoyAcceptCookie();
    createVisit();
  };

  ahoy.reset = function () {
    destroyCookie('ahoy_visit');
    destroyCookie('ahoy_visitor');
    destroyCookie('ahoy_events');
    destroyCookie('ahoy_track');
    return true;
  };

  window.ahoy = ahoy;

  onPageLoad(() => {
    ahoy.start();
    ahoy.trackView();
  });
}(window));
